import React, { FC, useState, useEffect } from "react";
import { SelectChangeEvent, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Stakeholders,
  QualitativeAssessment,
  DoubleMaterialityQuestions,
  DoubleMaterialityAssignStakeholders,
  DoubleMaterialityResults,
} from "./components";
import { CreateSurveyModal } from "./components/CreateSurveyModal";
import { getDMSteps } from "./DoubleMateriality.const";
import { DMSurveyEmptyState } from "./components/DMSurveyEmptyState/DMSurveyEmptyState";

import { CardGuide } from "@components/common";
import { Select } from "@components/base";
import { useGetSurveys } from "@api/hooks/doubleMateriality";
import { useUserStore } from "@store/user";

const CREATE_NEW_OPTION = "__create_new__";

const DoubleMateriality: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const initialYear = searchParams.get("year");
  const savedYear = localStorage.getItem("dm_preferred_year");
  const activeStep = parseInt(searchParams.get("step") || "0", 10);

  const { surveys, isPending: isLoadingSurveys } = useGetSurveys(user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<string>(
    initialYear || savedYear || String(new Date().getFullYear()),
  );

  const sortedSurveys = surveys?.sort((a, b) => b.year - a.year) || [];
  const existingYears = sortedSurveys.map((s) => s.year);
  const hasSurveys = sortedSurveys.length > 0;

  // Update selected year when surveys load or when year param changes
  useEffect(() => {
    if (hasSurveys && !isLoadingSurveys) {
      const yearParam = searchParams.get("year");
      const savedYear = localStorage.getItem("dm_preferred_year");

      if (yearParam) {
        const yearExists = existingYears.includes(parseInt(yearParam, 10));

        if (yearExists) {
          setSelectedYear(yearParam);
          // Update persistent storage if valid year in URL
          if (yearParam !== savedYear) {
            localStorage.setItem("dm_preferred_year", yearParam);
          }
        } else {
          // If URL has invalid year, redirect to most recent survey
          const mostRecentYear = String(sortedSurveys[0].year);

          setSelectedYear(mostRecentYear);
          navigate(
            `/csrd/double-materiality?year=${mostRecentYear}&step=${activeStep}`,
          );
        }
      } else {
        // If no year param, try localStorage, then fall back to most recent
        let targetYear = savedYear;
        const savedYearExists =
          savedYear && existingYears.includes(parseInt(savedYear, 10));

        if (!savedYearExists) {
          targetYear = String(sortedSurveys[0].year);
        }

        if (targetYear) {
          setSelectedYear(targetYear);
          navigate(
            `/csrd/double-materiality?year=${targetYear}&step=${activeStep}`,
          );
        }
      }
    }
  }, [
    hasSurveys,
    isLoadingSurveys,
    searchParams,
    sortedSurveys,
    existingYears,
    navigate,
    activeStep,
  ]);

  const steps = getDMSteps(t);

  const handleStepClick = (index: number): void => {
    navigate(`/csrd/double-materiality?step=${index}&year=${selectedYear}`);
  };

  const handleYearChange = (event: SelectChangeEvent<unknown>): void => {
    const value = event.target.value as string;

    if (value === CREATE_NEW_OPTION) {
      setIsModalOpen(true);

      return;
    }

    setSelectedYear(value);
    localStorage.setItem("dm_preferred_year", value);
    navigate(`/csrd/double-materiality?year=${value}&step=${activeStep}`);
  };

  const handleCreateSurvey = (): void => {
    setIsModalOpen(true);
  };

  const handleModalClose = (): void => {
    setIsModalOpen(false);
  };

  const handleSurveyCreated = (year: number): void => {
    const yearStr = String(year);

    setSelectedYear(yearStr);
    localStorage.setItem("dm_preferred_year", yearStr);
    navigate(`/csrd/double-materiality?year=${yearStr}&step=0`);
  };

  const yearSelector = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Typography variant="subtitle1">
        {t("doubleMateriality.year", "Year")}
      </Typography>
      <Select size="small" native value={selectedYear} onChange={handleYearChange}>
        {sortedSurveys.map((survey) => (
          <option key={survey.id} value={String(survey.year)}>
            {survey.year}
          </option>
        ))}
        <option disabled>{"──────────"}</option>
        <option value={CREATE_NEW_OPTION}>
          {"+ "}
          {t("doubleMateriality.createNewSurvey", "Add new survey year")}
        </option>
      </Select>
    </Stack>
  );

  // Show empty state if no surveys exist
  if (!hasSurveys && !isLoadingSurveys) {
    return (
      <CardGuide
        title={t("doubleMateriality.title")}
        titleTooltip={t("doubleMateriality.tooltip")}
        onClickStep={handleStepClick}
        active={activeStep}
        clicked={[true, true, true, true, true]}
        disabled={true}
        steps={steps}
      >
        <DMSurveyEmptyState onCreateSurvey={handleCreateSurvey} />
        <CreateSurveyModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleSurveyCreated}
          existingYears={existingYears}
        />
      </CardGuide>
    );
  }

  // Show loading state
  if (isLoadingSurveys) {
    return null;
  }

  return (
    <>
      <CardGuide
        title={t("doubleMateriality.title")}
        titleTooltip={t("doubleMateriality.tooltip")}
        onClickStep={handleStepClick}
        active={activeStep}
        steps={steps}
        clicked={[true, true, true, true, true]}
        rightTitleContent={yearSelector}
      >
        <Stack spacing={2} mt={3}>
          {activeStep === 0 && <Stakeholders />}
          {activeStep === 1 && <QualitativeAssessment />}
          {activeStep === 2 && <DoubleMaterialityQuestions />}
          {activeStep === 3 && <DoubleMaterialityAssignStakeholders />}
          {activeStep === 4 && <DoubleMaterialityResults />}
        </Stack>
      </CardGuide>

      <CreateSurveyModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleSurveyCreated}
        existingYears={existingYears}
      />
    </>
  );
};

export default DoubleMateriality;
